import { useCallback, useEffect, useMemo, useState } from 'react'

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Request } from 'commons/utils/request'
import { CTRDetailsRequest, CTRDetailsResponse } from '~bank-bsa-reporting/types'

export const CTR_DETAILS_KEY = 'ctr-details'

const useGetCTR = (opts: CTRDetailsRequest) =>
  useQuery({
    queryKey: [CTR_DETAILS_KEY, opts],
    queryFn: () => Request.post<CTRDetailsResponse, CTRDetailsRequest>(Request.urls.bank.ctrDetailsWebpage, opts),
    placeholderData: keepPreviousData
  })

const defaultFilter = (id: number): CTRDetailsRequest => ({
  historyPage: 1,
  historySize: 10,
  id
})

export const useGetCTRDetails = (id: number) => {
  const [filter, setFilter] = useState<CTRDetailsRequest>(defaultFilter(id))

  useEffect(() => setFilter(defaultFilter(id)), [id])

  const { data, isFetching } = useGetCTR(filter)

  const onHistoryFilterChange = useCallback(
    (page: number, size: number) => {
      if (page !== filter.historyPage || size !== filter.historySize) {
        setFilter({ ...filter, historyPage: page, historySize: size })
      }
    },
    [filter]
  )

  const historyTableConfig = useMemo(
    () => ({
      isFetching,
      filter: {
        page: filter.historyPage || 1,
        size: filter.historySize || 10
      },
      onFilterChange: onHistoryFilterChange
    }),
    [filter, onHistoryFilterChange, isFetching]
  )

  return { data, isFetching, historyTableConfig }
}
